import { parseISO } from 'date-fns'

export function formatOrder(order, included) {
  const { id, attributes, relationships } = order

  const billingProfileId = relationships.billing_profile?.data?.id
  const budgetId = relationships.budget?.data?.id

  const billingProfile = included?.find(item => item.id === billingProfileId && item.type === 'billing_profile')
  const budget = included?.find(item => item.id === budgetId && item.type === 'budget')

  return {
    id,
    deliveryDate: attributes.delivery_date,
    status: attributes.status_text,
    statusColor: attributes.status_text_color,
    statusMap: attributes.status_map,
    total: attributes.total_price,
    hasPickup: attributes.has_pickup,
    name: attributes.name,
    billingProfile: {
      id: billingProfileId,
      name: billingProfile?.attributes?.name,
      isInvoice: billingProfile?.attributes?.invoice_customer,
    },
    budget: {
      id: budgetId,
      name: budget?.attributes?.name,
    },
    totalPrice: attributes.total_price,
    deletable: attributes.deletable,
  }
}

export function formatRecurringOrder(order, included) {
  const { id, attributes, relationships } = order

  const billingProfileId = relationships.billing_profile?.data?.id

  const billingProfile = included?.find(item => item.id === billingProfileId && item.type === 'billing_profile')

  return {
    id,
    status: attributes.order_status,
    name: attributes.name,
    schedule: attributes.schedule,
    safeToDelete: attributes.safe_to_delete,
    billingProfile: {
      id: billingProfileId,
      name: billingProfile?.attributes?.name,
      isInvoice: billingProfile?.attributes?.invoice_customer,
    },
    archived: attributes.archived,
    unsafeToggleArchiveText: attributes.unsafe_toggle_archive_text,
  }
}

export function formatPagination(meta, links) {
  const page = meta.page

  return {
    currentPage: page?.page,
    totalPages: page?.pages,
    totalItems: page?.count,
    itemsPerPage: page?.items,
    firstPage: links.first,
    lastPage: links.last,
    prevPage: links.prev,
    nextPage: links.next,
  }
}

export function formatCorporateUser(data) {
  const { id, attributes } = data

  return {
    id,
    archived: attributes.archived,
    name: attributes.display_short_name,
    suiteName: attributes.suite_name,
    suiteId: attributes.suite_id,
    program: attributes.program,
    programName: attributes.program_display_name,
    nickname: attributes.nickname,
    nicknameId: attributes.nickname_id,
    craftyCreditTotal: attributes.crafty_credit_total,
    prepaymentTotal: attributes.prepayment_total,
    prepaymentPercent: attributes.prepayment_percent,
    icId: attributes.client_inventory_id,
    deliveryInstructions: attributes.delivery_instructions,
    speedyDeliveryCutoff: attributes.speedy_delivery_cutoff,
    icOrderMethod: attributes.ic_order_method,
    sendMissedItems: attributes.send_missed_items,
    pricingType: attributes.pricing_type,
    primaryBillingProfileId: attributes.primary_billing_profile_id,
    budgetNotes: attributes.budget_notes,
    mergeable: attributes.mergeable,
    programIcon: attributes.program_icon,
    defaultBudgetId: attributes.default_budget_id,
    safeToDelete: attributes.safe_to_delete,
    allowChains: attributes.allow_chains,
    overnight: attributes.overnight,
    lifecycleStatus: attributes.lifecycle_status,
  }
}

export function formatPartnerBranch(data) {
  return {
    id: data.id,
    name: data.name,
    partnerManagerName: data.partner_manager_name,
    cutoffTime: data.cutoff_time,
    selfOrdering: data.self_ordering,
    partnerBranchAccountId: data.partner_branch_account_id,
    menuActive: data.menu_active,
  }
}

export function formatBudget(data) {
  const { id, attributes } = data
  return {
    id,
    name: attributes.name,
    startDate: attributes.start_date,
    endDate: attributes.end_date,
    taxIncluded: attributes.tax_included,
    successMetric: {
      name: attributes?.success_metric?.name,
      color: attributes?.success_metric?.color,
      textColor: attributes?.success_metric?.text_color,
      icon: attributes?.success_metric?.icon,
      description: attributes?.success_metric?.description,
    },
  }
}

export function formatSuite(data) {
  const { id, attributes } = data

  return {
    id,
    name: attributes.name,
    buildingId: attributes.building_id,
  }
}

export function formatMembership(data) {
  const { id, attributes, relationships } = data

  return {
    id,
    serviceType: attributes?.service_type,
    deliveryFee: attributes?.delivery_fee,
    distributionCenterFee: attributes?.distribution_center_fee,
    buildingId: attributes?.building_id,
    buildingDisplayName: attributes?.building_display_name,
    taxCode: attributes?.tax_code,
    primaryLocationOwner: {
      id: attributes?.primary_location_owner?.id,
      firstName: attributes?.primary_location_owner?.first_name,
      lastName: attributes?.primary_location_owner?.last_name,
      fullName: attributes?.primary_location_owner?.full_name,
    },
    archived: attributes?.archived,
    distributionCenter: {
      id: relationships?.distribution_center?.data?.id,
    },
    accounts: relationships?.accounts?.data?.map(account => ({
      id: account.id,
    })),
  }
}

export function formatOrderDayChange(data) {
  const { id, attributes } = data

  return {
    id,
    originalDate: attributes?.original_date ? parseISO(attributes?.original_date) : null,
  }
}

export const deserialiseResponse = (resp) => {
  const included = getIncludedAPIMap(resp?.included || [])

  if (Array.isArray(resp.data)) {
    return resp.data.map(dataItem => formatItem(dataItem, included))
  } else {
    return formatItem(resp.data, included)
  }
}

export const getIncludedAPIMap = (included) => {
  return included?.reduce((acc, item) => {
    if (!acc.has(item.type)) {
      acc.set(item.type, new Map())
    }
    acc.get(item.type).set(Number(item.id), { id: Number(item.id), type: item.type, ...item.attributes, relationships: item.relationships })
    return acc
  }, new Map())
}

const createRelationshipAccessor = (relationship, included) => {
  if (!relationship) return null

  return {
    id: Number(relationship.id),
    type: relationship.type,
    getRecord: () => {
      const relatedItem = included.get(relationship.type)?.get(Number(relationship.id))
      return relatedItem ? formatItem(relatedItem, included) : null
    },
  }
}

const formatItem = (item, included) => {
  if (!item) return null

  const { id, type, relationships = {}, ...attributes } = item

  const formattedItem = {
    id: Number(id),
    type,
    ...attributes,
  }

  Object.keys(relationships).forEach(relKey => {
    const relationshipData = relationships[relKey].data

    if (Array.isArray(relationshipData)) {
      formattedItem[relKey] = relationshipData.map(rel => createRelationshipAccessor(rel, included))
    } else if (relationshipData) {
      formattedItem[relKey] = createRelationshipAccessor(relationshipData, included)
    }
  })

  return formattedItem
}


export function formatUnwrapped({ id, attributes }) {
  return {
    id,
    totalDeliveries: attributes.total_deliveries,
    totalUniqueIcProducts: attributes.total_unique_ic_products,
    topPantrySkus: attributes.top_pantry_skus,
    topBeverageSkus: attributes.top_beverage_skus,
    mostPopularSubcategories: attributes.most_popular_subcategories,
    totalMerchandisingTaskShiftMinutes: attributes.total_merchandising_task_shift_minutes,
    totalEquipmentTaskShiftMinutes: attributes.total_equipment_task_shift_minutes,
    totalStorageTaskShiftMinutes: attributes.total_storage_task_shift_minutes,
    assignedMemberships: attributes.assigned_memberships,
    assignedTeamMembers: attributes.assigned_team_members,
  }
}

export function formatCraftyHoliday(data) {
  const { id, attributes } = data

  return {
    id,
    name: attributes.name,
    date: attributes.date,
    monthDay: attributes.month_day,
    year: attributes.year,
    international: attributes.international,
    active: attributes.active,
  }
}

export function formatClosure(data) {
  const { id, attributes } = data

  return {
    id,
    startDate: attributes.start_date,
    endDate: attributes.end_date,
    monthDayRange: attributes.month_day_range,
    createdByClient: attributes.created_by_client,
    closureType: attributes?.closure_type,
    closureReason: attributes?.closure_reason,
    companyId: attributes?.company_id,
    companyName: attributes?.company_name,
    distributionCenterId: attributes?.distribution_center_id,
  }
}

export function formatSchedule({ id, attributes }) {
  const dates = Object.keys(attributes.events)

  const formattedEvents = dates.reduce((acc, date) => {
    const day = attributes.events[date]

    acc.set(date, {
      deliveryDay: day.delivery_day,
      canceledDeliveryDay: day.canceled_delivery_day,
      orderDay: day.order_day,
      canceledOrderDay: day.canceled_order_day,
      orderIds: day.order_ids,
      closures: day.closures,
      shifts: day.shifts.map(({ shift_id, occurrence_unique_id, name, start_time, end_time, timezone, overnight }) =>
        ({ shiftId: shift_id, occurrenceUniqueId: occurrence_unique_id, name, timezone, overnight, startTime: start_time, endTime: end_time })),
      canceledShifts: day.canceled_shifts.map(({ name, start_time, end_time, timezone, overnight }) =>
        ({ id, name, timezone, overnight, startTime: start_time, endTime: end_time })),
    })
    return acc
  }, new Map())

  return {
    id,
    events: formattedEvents,
  }
}

export function formatOrderBudget(data, included) {
  const { id, attributes } = data
  const budgetsUsers = included?.filter(item => item.type === 'budgets_user').map(item => ({ id: item.id, ...item.attributes }))

  return {
    id,
    name: attributes.name,
    success_metric_name: attributes.success_metric?.name,
    success_metric_type: attributes.success_metric_type,
    color: attributes.success_metric?.color,
    text_color: attributes.success_metric?.text_color,
    icon: attributes.success_metric?.icon,
    description: attributes.success_metric?.description,
    target: Number(attributes.order_target_information?.order_target),
    current_day_spend: Number(attributes.order_target_information?.current_day_spend),
    remaining_total: Number(attributes.order_target_information?.remaining_total),
    thresholds: attributes.order_target_information?.thresholds,
    budgets_users: budgetsUsers,
    archived: attributes.archived,
  }
}

export function formatCart(data) {
  const { attributes } = data
  return {
    editable: attributes?.editable,
    numItems: attributes?.num_items,
    subtotal: attributes?.subtotal,
    quantityByPurchasingSizeId: new Map(Object.entries(attributes?.quantity_by_dp)),
    suppliers: attributes?.suppliers,
    hasRecentlyOrderedProducts: attributes?.has_recently_ordered_products,
    stockingIncluded: attributes?.stocking_included,
    showInternalAddToCart: attributes?.show_internal_add_to_cart,
  }
}
